import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { outcomeLabel, xhrJson } from './outcomeApi';

function EditRow({ outcome, onSave, onCancel }) {
  const [text, setText] = useState(outcome.note || '');
  const id = outcome.outcome_id;
  return (
    <div className="mt-1 flex gap-1" data-testid={`edit-row-${id}`}>
      <textarea value={text} onChange={e => setText(e.target.value)} className="flex-1 border rounded p-1 text-xs"
        rows={2} data-testid={`edit-textarea-${id}`} autoFocus />
      <div className="flex flex-col gap-1">
        <button onClick={() => onSave(text)} data-testid={`save-edit-${id}`}
          className="px-2 py-0.5 bg-orange-500 hover:bg-orange-600 text-white rounded text-xs">Save</button>
        <button onClick={onCancel} data-testid={`cancel-edit-${id}`}
          className="px-2 py-0.5 border rounded text-xs hover:bg-muted">Cancel</button>
      </div>
    </div>
  );
}

function OutcomeItem({ h, editing, onEdit, onDelete, onSave, onCancel }) {
  return (
    <div className="text-xs bg-white border rounded p-2" data-testid={`outcome-${h.outcome_id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="font-semibold">{outcomeLabel(h.outcome_type)}
          {h.potential_referrals != null && <span className="ml-2 text-emerald-700">· {h.potential_referrals} referrals</span>}
          {h.edited_at && <span className="ml-2 text-amber-600 font-normal">(edited)</span>}
        </div>
        <div className="flex gap-1 opacity-80 hover:opacity-100">
          <button onClick={onEdit} data-testid={`edit-outcome-${h.outcome_id}`} title="Edit note"
            className="px-1.5 py-0.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground">✏ Edit</button>
          <button onClick={onDelete} data-testid={`delete-outcome-${h.outcome_id}`} title="Delete outcome"
            className="px-1.5 py-0.5 rounded hover:bg-red-50 text-muted-foreground hover:text-red-600">🗑 Delete</button>
        </div>
      </div>
      {editing ? <EditRow outcome={h} onSave={onSave} onCancel={onCancel} />
        : h.note && <div className="text-muted-foreground mt-0.5">"{h.note}"</div>}
      <div className="text-muted-foreground mt-1">
        {h.meeting_at && <>Meeting: {new Date(h.meeting_at).toLocaleString()} · </>}
        {h.followup_at && <>Follow-up: {new Date(h.followup_at).toLocaleDateString()} · </>}
        {h.logged_by} on {new Date(h.created_at).toLocaleString()}
        {h.edited_by && h.edited_at && <> · edited by {h.edited_by} on {new Date(h.edited_at).toLocaleDateString()}</>}
      </div>
    </div>
  );
}

export function OutcomeHistory({ partnerId, refreshKey = 0, onChanged }) {
  const [items, setItems] = useState(null);
  const [editingId, setEditingId] = useState(null);

  const load = () => xhrJson('GET', `/admin/reengagement/outcomes/${partnerId}`, null, (s, d) => {
    if (s === 200) setItems(d.outcomes || []);
  });
  useEffect(() => {
    load();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partnerId, refreshKey]);

  const save = (id, note) => xhrJson('PATCH', `/admin/reengagement/outcomes/${id}`, { note }, (s) => {
    if (s === 200) { toast.success('Note updated'); setEditingId(null); load(); onChanged?.(); }
    else toast.error(s === 403 ? 'Only the author or a super admin can edit' : 'Failed to update');
  });

  const remove = (id) => {
    if (!window.confirm('Delete this outcome? Partner suppression state (cold / cooldown) will be recomputed from the remaining history.')) return;
    xhrJson('DELETE', `/admin/reengagement/outcomes/${id}`, null, (s) => {
      if (s === 200) { toast.success('Outcome deleted'); load(); onChanged?.(); }
      else toast.error(s === 403 ? 'Only the author or a super admin can delete' : 'Failed to delete');
    });
  };

  if (!items) return <div className="text-xs text-muted-foreground">Loading...</div>;
  if (items.length === 0) return <div className="text-xs text-muted-foreground" data-testid="outcome-history-empty">No outreach yet.</div>;
  return (
    <div className="space-y-2" data-testid={`outcome-history-${partnerId}`}>
      {items.map(h => (
        <OutcomeItem key={h.outcome_id} h={h} editing={editingId === h.outcome_id}
          onEdit={() => setEditingId(h.outcome_id)} onCancel={() => setEditingId(null)}
          onSave={(note) => save(h.outcome_id, note)} onDelete={() => remove(h.outcome_id)} />
      ))}
    </div>
  );
}
